import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

/**
 * Camada de acesso a dados do Núcleo — tabela `pessoas` (migration 0016/0017).
 *
 * Esta é a versão Núcleo de `perfisService`/`usuariosService`. Durante a
 * Fase 4 (expand-contract, decisão D3), `perfis` continua existindo e é
 * mantida em sincronia automaticamente por trigger no banco sempre que uma
 * pessoa é criada ou atualizada aqui — não é responsabilidade deste service
 * escrever em `perfis` diretamente.
 */
export type Pessoa = Database["public"]["Tables"]["pessoas"]["Row"];
type PessoaInsert = Database["public"]["Tables"]["pessoas"]["Insert"];
type PessoaUpdate = Database["public"]["Tables"]["pessoas"]["Update"];

export const pessoasService = {
  /** Busca a pessoa vinculada a um usuário autenticado (auth.users.id). */
  async buscarPorUserId(userId: string): Promise<Pessoa> {
    const { data, error } = await supabase
      .from("pessoas")
      .select("*")
      .eq("user_id", userId)
      .single();

    if (error) {
      throw new Error("Não foi possível carregar as informações da pessoa.");
    }

    return data;
  },

  async listarTodas(): Promise<Pessoa[]> {
    const { data, error } = await supabase
      .from("pessoas")
      .select("*")
      .order("nome_completo", { ascending: true });

    if (error) throw new Error("Não foi possível carregar a lista de pessoas.");
    return data || [];
  },

  /**
   * Pessoas COM login (usuários — doc 03 §4). É o que a tela Usuários lista
   * (visitantes, sem login, vivem no elo Visitantes).
   */
  async listarComLogin(): Promise<Pessoa[]> {
    const { data, error } = await supabase
      .from("pessoas")
      .select("*")
      .not("user_id", "is", null)
      .order("nome_completo", { ascending: true });

    if (error) throw new Error("Não foi possível carregar a lista de usuários.");
    return data || [];
  },

  async listarPendentes(): Promise<Pessoa[]> {
    const { data, error } = await supabase
      .from("pessoas")
      .select("*")
      .eq("status", "pendente")
      .order("created_at", { ascending: true });

    if (error) throw new Error("Não foi possível carregar os cadastros pendentes.");
    return data || [];
  },

  async atualizar(id: string, dados: PessoaUpdate): Promise<void> {
    const { error } = await supabase.from("pessoas").update(dados).eq("id", id);
    if (error) throw new Error("Não foi possível atualizar a pessoa.");
  },

  /** Pendente -> ativo. Usado para aprovar um cadastro novo. */
  async aprovar(id: string): Promise<void> {
    const { error } = await supabase.from("pessoas").update({ status: "ativo" }).eq("id", id);
    if (error) throw new Error("Não foi possível aprovar a pessoa.");
  },

  /** Ativo -> inativo. Usado para desabilitar uma pessoa já aprovada. */
  async inativar(id: string): Promise<void> {
    const { error } = await supabase.from("pessoas").update({ status: "inativo" }).eq("id", id);
    if (error) throw new Error("Não foi possível inativar a pessoa.");
  },

  /** Inativo -> ativo. Usado para reabilitar uma pessoa que tinha sido inativada. */
  async reativar(id: string): Promise<void> {
    const { error } = await supabase.from("pessoas").update({ status: "ativo" }).eq("id", id);
    if (error) throw new Error("Não foi possível reativar a pessoa.");
  },

  /** Cria a pessoa diretamente (usado por criarComoAdmin após o signUp do Auth). */
  async criar(dados: PessoaInsert): Promise<Pessoa> {
    const { data, error } = await supabase.from("pessoas").insert(dados).select().single();
    if (error) throw new Error("Não foi possível salvar o cadastro da pessoa.");
    return data;
  },
};
